import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import InfoTooltip from 'components/InfoTooltip';

import PageHeader from 'components/PageHeader';
import { Tooltip } from 'components/ui/tooltip';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { FeatureFlag } from 'types/features';
import {
    buildPortfolioScopeUrlParams,
    parsePortfolioScopeParams,
    PORTFOLIO_SCOPE_PARAM_KEYS,
    PortfolioScopeState,
} from 'types/portfolio';
import { useHasPermissions } from 'utils/permissionUtil';
import { VIEW_PARAM_KEY } from 'utils/viewParam';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import MyPortfolioSummaryCards from './MyPortfolioSummaryCards';
import MyPortfolioTable from './MyPortfolioTable';
import {
    buildHeadingTooltipIntro,
    buildPortfolioHeading,
    INITIAL_SEARCH_PARAMS,
    portfolioScopeKey,
} from './portfolioScope';

// The two view-mode paragraphs are scope-neutral; only the opening paragraph names whose portfolio
// this is, so it comes from the scope (see buildHeadingTooltipIntro).
const VIEW_MODE_TOOLTIP_TEXT = ['portfolio.heading-tooltip.groups-view', 'portfolio.heading-tooltip.contacts-view'];

// Privilege required to view any portfolio other than the caller's own.
const PORTFOLIO_VIEW_OTHER = 'PORTFOLIO.VIEW_OTHER';

const MyPortfolioPage = () => {
    // Scope lives in the URL so it is shareable, survives reload/back, and is saved by
    // the list table's filter-persistence system. The People picker only writes ids to the URL, so the
    // resolved colleague names are kept in a small cache and re-applied for the heading/legend.
    const [searchParams, setSearchParams] = useSearchParams();
    const [nameCache, setNameCache] = useState<Record<number, string>>({});

    const urlScope = useMemo(() => parsePortfolioScopeParams(searchParams), [searchParams]);
    const scope = useMemo<PortfolioScopeState>(
        () => ({
            scope: urlScope.scope,
            people: urlScope.people.map((person) => ({ id: person.id, name: nameCache[person.id] ?? person.name })),
        }),
        [urlScope, nameCache]
    );

    // Both hooks must run unconditionally (rules of hooks); the selector renders only when the
    // flag is on AND the caller holds PORTFOLIO.VIEW_OTHER.
    const isScopeFlagEnabled = useIsFeatureEnabled(FeatureFlag.PORTFOLIO_SCOPE_SELECTOR);
    const canViewOtherPortfolios = useHasPermissions([PORTFOLIO_VIEW_OTHER]);
    const canUseScopeSelector = isScopeFlagEnabled && canViewOtherPortfolios;

    const handleScopeChange = useCallback(
        (next: PortfolioScopeState) => {
            // Remember colleague names the picker has resolved so they outlast the URL round-trip
            // (the URL only stores ids).
            setNameCache((prev) => {
                let changed = false;
                const updated = { ...prev };
                next.people.forEach((person) => {
                    if (person.name && person.name !== String(person.id) && updated[person.id] !== person.name) {
                        updated[person.id] = person.name;
                        changed = true;
                    }
                });
                return changed ? updated : prev;
            });

            const scopeTypeChanged = next.scope !== urlScope.scope;
            const scopeParams = buildPortfolioScopeUrlParams(next);
            setSearchParams(
                (prev) => {
                    // AC: a scope-TYPE change resets to the default sort/filter state (same as a fresh
                    // page load); adding/removing people within "People" keeps the active filters and
                    // only swaps the scope params.
                    const params = scopeTypeChanged
                        ? new URLSearchParams(INITIAL_SEARCH_PARAMS)
                        : new URLSearchParams(prev);
                    // Looking at the same list through someone else's portfolio is still the same
                    // view, so the reset above must not throw the Groups/Contacts choice away.
                    const view = prev.get(VIEW_PARAM_KEY);
                    if (view) params.set(VIEW_PARAM_KEY, view);
                    PORTFOLIO_SCOPE_PARAM_KEYS.forEach((key) => params.delete(key));
                    if (scopeParams.scope) params.set('scope', scopeParams.scope);
                    if (scopeParams.userIds) params.set('userIds', scopeParams.userIds);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams, urlScope.scope]
    );

    const heading = buildPortfolioHeading(scope);
    const headingTooltipIntro = buildHeadingTooltipIntro(scope);
    const headingTooltipText = headingTooltipIntro
        ? [headingTooltipIntro.id, ...VIEW_MODE_TOOLTIP_TEXT]
        : VIEW_MODE_TOOLTIP_TEXT;

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <PageHeader
                    hostsList
                    title={heading}
                    titleNode={
                        <Tooltip content={heading}>
                            <span className="block max-w-[48ch] truncate">{heading}</span>
                        </Tooltip>
                    }
                    showBreadcrumb
                    iconsEnd={
                        <InfoTooltip
                            text={headingTooltipText}
                            values={headingTooltipIntro?.values}
                            className="h-[22px]"
                        />
                    }
                />
                <MyPortfolioSummaryCards scope={scope} />
                <MyPortfolioTable
                    key={portfolioScopeKey(scope)}
                    scope={scope}
                    onScopeChange={handleScopeChange}
                    canUseScopeSelector={canUseScopeSelector}
                />
            </div>
        </PaddedLayout>
    );
};

export default MyPortfolioPage;
